import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  List,
  Typography,
  Row,
  Col,
  Avatar,
  DatePicker,
  message,
  Skeleton,
  Divider,
  Timeline,
  Tag,
  Space,
  Collapse,
  Modal,
  Tooltip,
} from "antd";
import {
  ShoppingOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SendOutlined,
  CloseCircleOutlined,
  ShopOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  DownloadOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import MarketPlaceService from "../../../utils/api/marketplace";
import UserService from "../../../utils/api/users";
import NotificationService from "../../../utils/api/notifications";
import MarketplaceStatus from "../MarketplaceStatus";
import { OrderDataStatusLogs } from "../data/OrderDataStatusLog";
import LayoutPage from "../../../layouts/Main";
import TrackingTimeline from "./TrackingTimeline";
import useAppStore from "../../../store/useAppStore";
import "../../../scss/OrdersDetails.css";

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { confirm } = Modal;

const OrdersDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = Number(localStorage.getItem("role"));
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [orderLogs, setOrderLogs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [isDeliveryDateLocked, setIsDeliveryDateLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [activeKey, setActiveKey] = useState([]);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [isLoginGateModalOpen, setIsLoginGateModalOpen] = useState(false);
  const [isAccessBlocked, setIsAccessBlocked] = useState(false);
  const [labelModalVisible, setLabelModalVisible] = useState(false);
  const [labelDataUri, setLabelDataUri] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState(null);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [trackingModalVisible, setTrackingModalVisible] = useState(false);
  const [shipmentId, setShipmentId] = useState(null);
  const [trackingNumberState, setTrackingNumberState] = useState(null);
  const sentDeliveryAlertsRef = useRef(new Set());
  const addNotification = useAppStore((state) => state.addNotification);

  const { clientStatusLogs, adminStatusLogs, supplierStatusLogs } =
    OrderDataStatusLogs;

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const rawLoginFlag = (
      query.get("login") ||
      query.get("requireLogin") ||
      ""
    ).toLowerCase();
    const shouldRequestLogin =
      rawLoginFlag === "1" || rawLoginFlag === "true" || rawLoginFlag === "yes";
    const enforceLoginOnRoute = true;
    const shouldValidateSession = enforceLoginOnRoute || shouldRequestLogin;

    if (!shouldValidateSession) return;

    let parsedUser = null;
    try {
      parsedUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch (error) {
      parsedUser = null;
    }

    const hasUserSession = Boolean(
      parsedUser?.id && localStorage.getItem("role")
    );
    const hasToken = Boolean(localStorage.getItem("token"));
    const hasSession = hasUserSession || hasToken;

    if (hasSession) {
      setIsAccessBlocked(false);
      setIsLoginGateModalOpen(false);
      return;
    }

    const explicitRedirect = query.get("redirect") || query.get("redirectTo");
    const cleanedQuery = new URLSearchParams(location.search);
    ["login", "requireLogin", "redirect", "redirectTo"].forEach((param) =>
      cleanedQuery.delete(param)
    );
    const cleanSearch = cleanedQuery.toString();
    const currentPagePath = `${location.pathname}${
      cleanSearch ? `?${cleanSearch}` : ""
    }${location.hash || ""}`;

    localStorage.setItem(
      "post_login_redirect",
      explicitRedirect || currentPagePath
    );
    setIsAccessBlocked(true);
    setIsLoginGateModalOpen(true);
    setLoadingPage(false);
  }, [location.hash, location.pathname, location.search]);

  const getStatusMessage = () => {
    if (!order) return null;
    if ([1, 3, 5].includes(role)) {
      const found = adminStatusLogs.find((o) => o.status === order.status);
      return found?.text || null;
    }
    if (role === 6) {
      const found = supplierStatusLogs.find((o) => o.status === order.status);
      return found?.text || null;
    }
    const found = clientStatusLogs.find((o) => o.status === order.status);
    return found?.text || null;
  };

  const getEffectiveDeliveryDate = () => deliveryDate || order?.deliveryDate;

  const getDeliveryAlertMeta = () => {
    const effectiveDeliveryDate = getEffectiveDeliveryDate();

    if (!effectiveDeliveryDate || !order || order.status === 3 || order.status === 4) {
      return null;
    }

    const today = dayjs().startOf("day");
    const dueDate = dayjs(effectiveDeliveryDate).startOf("day");

    if (!dueDate.isValid()) return null;

    const daysOverdue = today.diff(dueDate, "day");
    if (daysOverdue >= 2) {
      return {
        key: "overdue",
        tagColor: "red",
        label: `Retraso: ${daysOverdue} ${daysOverdue === 1 ? "día" : "días"}`,
        title: "Orden con retraso de entrega",
        description: `La orden ${order.id} tiene ${daysOverdue} ${daysOverdue === 1 ? "día" : "días"} de retraso respecto a la fecha de entrega.`,
      };
    }

    const daysToDueDate = dueDate.diff(today, "day");
    if (daysToDueDate >= 0 && daysToDueDate <= 2) {
      return {
        key: "near_due",
        tagColor: "orange",
        label: "Entregar pronto",
        title: "Fecha de entrega cercana",
        description: `La orden ${order.id} tiene una fecha de entrega cercana (${dueDate.format("DD/MM/YYYY")}).`,
      };
    }

    return null;
  };

  const notifyDeliveryAlert = async (alertMeta) => {
    if (!alertMeta || !order) return;

    const effectiveDeliveryDate = getEffectiveDeliveryDate();
    const alertKey = `${order.id}:${effectiveDeliveryDate}:${alertMeta.key}`;

    if (sentDeliveryAlertsRef.current.has(alertKey)) {
      return;
    }

    try {
      const supplierIds = [
        ...new Set(
          (order.products || [])
            .map((product) => product.supplier_id || product.supplierId)
            .filter(Boolean)
        ),
      ];

      let adminIds = [];
      try {
        const adminResponse = await UserService.GetUsersByRoles([1]);
        const adminData = adminResponse?.data;
        const admins = Array.isArray(adminData)
          ? adminData
          : Array.isArray(adminData?.users)
          ? adminData.users
          : [];
        adminIds = admins.map((admin) => admin.id).filter(Boolean);
      } catch (adminError) {
        console.error("Error loading admin recipients:", adminError);
      }

      const recipientIds = [...new Set([...supplierIds, ...adminIds])];
      const currentUserId = (() => {
        try {
          return JSON.parse(localStorage.getItem("user") || "null")?.id;
        } catch (error) {
          return null;
        }
      })();

      if (recipientIds.length > 0) {
        const results = await NotificationService.CreateNotificationsForUsers(
          recipientIds,
          {
          title: alertMeta.title,
          description: alertMeta.description,
          status: "warning",
          marketplaceOrderId: order.id,
          order_marketplace_id: order.id,
          orderId: order.id,
          }
        );

        const hasSuccess = Array.isArray(results)
          ? results.some((result) => result.status === "fulfilled")
          : false;

        if (!hasSuccess) {
          console.error("No notification endpoint accepted delivery alert payload");
        }
      }

      if (currentUserId && recipientIds.includes(currentUserId)) {
        addNotification({
          id: `local-${alertKey}`,
          title: alertMeta.title,
          description: alertMeta.description,
          seen: false,
          createdAt: new Date().toISOString(),
          route: `/marketplace/orders/details/${encodeURIComponent(order.id)}`,
        });
      }

      sentDeliveryAlertsRef.current.add(alertKey);
    } catch (error) {
      console.error("Error sending delivery alert notifications:", error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 1:
        return <ClockCircleOutlined />;
      case 2:
        return <SendOutlined />;
      case 3:
        return <CheckCircleOutlined />;
      case 4:
        return <CloseCircleOutlined />;
      default:
        return <ShoppingOutlined />;
    }
  };

  const getBadgeColor = (status) => {
    switch (status) {
      case 1:
        return "#faad14";
      case 2:
        return "#fa8c16";
      case 3:
        return "#52c41a";
      case 4:
        return "#f5222d";
      default:
        return "#d9d9d9";
    }
  };

  const loadOrderData = async () => {
    try {
      const orderResponse = await MarketPlaceService.getOrderById(id);
      const orderData = orderResponse.data;
      setOrder(orderData);

      const orderLogsResponse = await MarketPlaceService.getLogsByOrder(id);
      setOrderLogs(orderLogsResponse.data);
    } catch (error) {
      console.error("Error loading order data:", error);
    }
  };

  useEffect(() => {
    if (isAccessBlocked) {
      return undefined;
    }

    const fetchOrderAndSuppliers = async () => {
      try {
        const orderResponse = await MarketPlaceService.getOrderById(id);
        const orderData = orderResponse.data;
        setOrder(orderData);

        const orderLogsResponse = await MarketPlaceService.getLogsByOrder(id);
        setOrderLogs(orderLogsResponse.data);

        const uniqueSuppliers = [];
        const suppliersSet = new Set();

        for (const product of orderData.products) {
          if (!suppliersSet.has(product.supplier_id)) {
            const supplierResponse = await UserService.GetUserById({
              id: product.supplier_id,
            });
            uniqueSuppliers.push(supplierResponse.data);
            suppliersSet.add(product.supplier_id);
          }
        }
        setSuppliers(uniqueSuppliers);
        setLoading(false);
        setLoadingPage(false);
      } catch (error) {
        setLoading(false);
        setLoadingPage(false);
      }
    };

    fetchOrderAndSuppliers();

    return () => {
      setOrderLogs([]);
      setOrder(null);
      setSuppliers([]);
    };
  }, [id, isAccessBlocked]);

  useEffect(() => {
    const alertMeta = getDeliveryAlertMeta();
    if (!alertMeta) return;

    notifyDeliveryAlert(alertMeta);
  }, [order, deliveryDate]);

  const handleLoginRedirect = () => {
    navigate("/login");
  };

  const handleRegisterRedirect = () => {
    navigate("/register");
  };

  const updateOrderStatus = async (newStatus) => {
    if (!order) return;

    const effectiveDeliveryDate = deliveryDate || order?.deliveryDate;
    if (newStatus === 2 && !effectiveDeliveryDate) {
      message.error("Debes capturar la fecha de entrega antes de continuar.");
      return;
    }

    setStatusChangeLoading(true);

    const previousStatus = order.status;

    setOrder((prevOrder) => ({
      ...prevOrder,
      status: newStatus,
    }));

    try {
      const body = { status: newStatus };

      if (effectiveDeliveryDate && newStatus !== 3) {
        body.deliveryDate = effectiveDeliveryDate;
      }

      await MarketPlaceService.updateOrder(body, order.id);

      let statusText = "";
      switch (newStatus) {
        case 1:
          statusText = "aceptada";
          break;
        case 2:
          statusText = 'marcada como "En camino"';
          break;
        case 3:
          statusText = 'marcada como "Entregada"';
          break;
        case 4:
          statusText = "cancelada";
          break;
        case 5:
          statusText = "rechazada";
          break;
        default:
          statusText = "actualizada";
      }

      message.success(`Orden ${statusText} correctamente`);

      await loadOrderData();
    } catch (error) {

      if (error.response?.status === 500) {
        message.warning(
          "La orden se actualizó pero hubo un problema en la respuesta del servidor"
        );
        await loadOrderData();
      } else {
        message.error("Error al actualizar el estado de la orden");
        setOrder((prevOrder) => ({
          ...prevOrder,
          status: previousStatus,
        }));
      }
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleStatusClick = () => {
    if (role === 6 && order) {
      if (order.status === 9) {
        setStatusModalVisible(true);
      } else if (order.status === 1) {
        handleMarkAsInTransit();
      } else if (order.status === 2) {
        handleMarkAsDelivered();
      }
    }
  };

  const handleMarkAsInTransit = () => {
    confirm({
      title: '¿Marcar orden como "En camino"?',
      icon: <ExclamationCircleOutlined />,
      content: `La orden ${order.id} será marcada como "En camino".`,
      okText: "Sí, confirmar",
      cancelText: "Cancelar",
      onOk: () => updateOrderStatus(2),
    });
  };

  const handleMarkAsDelivered = () => {
    confirm({
      title: '¿Marcar orden como "Entregada"?',
      icon: <ExclamationCircleOutlined />,
      content: `La orden ${order.id} será marcada como "Entregada". ¿Estás seguro de que la orden fue entregada correctamente?`,
      okText: "Sí, confirmar entrega",
      cancelText: "Cancelar",
      onOk: () => updateOrderStatus(3),
    });
  };

  const handleStatusModalClose = () => {
    setStatusModalVisible(false);
    setStatusChangeLoading(false);
  };

  const handleStatusChange = async (newStatus) => {
    if (!order) return;
    await updateOrderStatus(newStatus);
    handleStatusModalClose();
  };

  const showStatusConfirm = (action, status) => {
    confirm({
      title: `¿Estás seguro de que deseas ${action} esta orden?`,
      icon: <ExclamationCircleOutlined />,
      content: `La orden ${order.id} será ${
        action === "aceptar" ? "aceptada" : "rechazada"
      }.`,
      okText: "Sí, confirmar",
      cancelText: "Cancelar",
      okType: action === "aceptar" ? "primary" : "danger",
      onOk: () => handleStatusChange(status),
      onCancel: handleStatusModalClose,
    });
  };

  const handleDateChange = (date) => {
    if (!date) {
      setDeliveryDate(null);
      setIsDeliveryDateLocked(false);
      return;
    }

    const today = dayjs();
    if (!date.isAfter(today, "day")) {
      message.error("La fecha de entrega debe ser mayor a la fecha actual.");
      setDeliveryDate(null);
      setIsDeliveryDateLocked(false);
    } else {
      setDeliveryDate(date.format("YYYY-MM-DD"));
      setIsDeliveryDateLocked(true);
    }
  };

  const handleEnableDeliveryDateEdit = () => {
    setIsDeliveryDateLocked(false);
  };

  const getStatusClickable = () => {
    if (role === 6 && order) {
      return [9, 1, 2].includes(order.status);
    }
    return false;
  };

  const getStatusTooltip = () => {
    if (role !== 6 || !order) return "";

    switch (order.status) {
      case 9:
        return "Haz clic para aceptar o rechazar la orden";
      case 1:
        return "Haz clic para marcar como 'En camino'";
      case 2:
        return "Haz clic para marcar como 'Entregada'";
      default:
        return "";
    }
  };

  const getOrderProvider = () => {
    if (!order || !order.products || order.products.length === 0) return null;

    const firstProduct = order.products[0];
    const supplier = suppliers.find((s) => s.id === firstProduct.supplier_id);

    return {
      name: supplier?.name || "Proveedor",
      email: supplier?.email || "servicioalcliente@epno.com.mx",
      phone: supplier?.phone || "6567038586",
      addressLine: supplier?.address || "C. isaac Newton 1650, Bermúdez",
      city: supplier?.city || "Juárez",
      zipCode: supplier?.zipcode || "32470",
      country: "MX",
    };
  };

  const getOrderCustomer = () => {
    if (!order) return null;

    return {
      name: order.client_name || "Cliente",
      email: order.client_email || "cliente@correo.com",
      phone: order.client_phone || "8119876543",
      addressLine:
        order.shipping_address || order.client_address || "Calle Destino 456",
      city: order.shipping_city || order.client_city || "Monterrey",
      zipCode: order.shipping_zipcode || order.client_zipcode || "64000",
      country: "MX",
    };
  };

  const calculateTotalWeight = () => {
    if (!order || !order.products || order.products.length === 0) return 1.0;

    let totalWeight = 0;
    order.products.forEach((product) => {
      const weight = product.weight || product.peso || 0.5;
      const quantity = product.quantity || product.cantidad || 1;
      totalWeight += weight * quantity;
    });

    return Math.max(totalWeight, 0.5);
  };

  const hasDHLGuide = () => {
    if (!order || !order.products) return false;
    return order.products.some((product) => product.dhl_shipment_id);
  };

  const getDHLGuideNumbers = () => {
    if (!order || !order.products) return [];
    return order.products
      .filter((product) => product.dhl_shipment_id)
      .map((product) => ({
        productName: product.name,
        guideNumber: product.dhl_shipment_id,
      }));
  };

  const getShippingCost = () => {
    if (!order) return null;

    if (order.shipping_cost !== undefined && order.shipping_cost !== null) {
      return Number(order.shipping_cost);
    }
    if (
      order.shipping_info?.cost !== undefined &&
      order.shipping_info?.cost !== null
    ) {
      return Number(order.shipping_info.cost);
    }
    return null;
  };

  const getShippingService = () => {
    if (!order) return null;

    if (order.shipping_service) {
      return order.shipping_service;
    }
    if (order.shipping_info?.service) {
      return order.shipping_info.service;
    }
    return null;
  };

  const getSubtotal = () => {
    if (!order) return 0;

    if (order.subtotal !== undefined && order.subtotal !== null) {
      return Number(order.subtotal);
    }
    if (order.price !== undefined && order.price !== null) {
      return Number(order.price);
    }
    return 0;
  };

  const getTotalAmount = () => {
    if (!order) return 0;

    if (order.total !== undefined && order.total !== null) {
      return Number(order.total);
    }
    const subtotal = getSubtotal();
    const shippingCost = getShippingCost() || 0;
    return subtotal + shippingCost;
  };

  const handleGenerateLabel = async () => {
    setGeneratingLabel(true);
    try {
      const provider = getOrderProvider();
      const customer = getOrderCustomer();
      const totalWeight = calculateTotalWeight();

      if (!provider || !customer) {
        throw new Error(
          "No se pudo obtener la información del proveedor o cliente"
        );
      }

      const firstProduct = order.products && order.products[0];
      if (!firstProduct) {
        throw new Error("No hay productos en esta orden");
      }

      const payload = {
        shipper_name: provider.name,
        shipper_email: provider.email,
        shipper_phone: provider.phone,
        shipper_address: provider.addressLine,
        shipper_city: provider.city,
        shipper_postal_code: provider.zipCode,
        shipper_country: provider.country,

        recipient_name: customer.name,
        recipient_email: customer.email,
        recipient_phone: customer.phone,
        recipient_address: customer.addressLine,
        recipient_city: customer.city,
        recipient_postal_code: customer.zipCode,
        recipient_country: customer.country,

        weight: totalWeight,
        dimensions: { length: 20, width: 20, height: 15 },
        content_description: `Artículos de Orden #${order.id}`,
        reference_number: order.id,
        order_id: order.id,
        product_id: firstProduct.id || firstProduct.product_id,
      };


      const response = await MarketPlaceService.generateDHLLabel(payload);


      let data = response;
      if (response && response.data) {
        data = response.data;
      }

      if (data && data.success === true) {
        const trackingNum = data.dhl_tracking_number || data.tracking_number;
        const labelUri = data.label_url;

        if (!trackingNum || !labelUri) {
          throw new Error("La respuesta no contiene los datos esperados");
        }

        setTrackingNumber(trackingNum);
        setLabelDataUri(labelUri);
        setLabelModalVisible(true);

        message.success(`Guía generada exitosamente! Tracking: ${trackingNum}`);

        await loadOrderData();
      } else {
        throw new Error(data?.message || "No se pudo generar la guía");
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Hubo un error al conectar con DHL";
      message.error(errorMsg);
    } finally {
      setGeneratingLabel(false);
    }
  };

  const handlePrintLabel = () => {
    if (!labelDataUri) {
      message.error("No hay guía para imprimir");
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      message.error("Por favor, permite las ventanas emergentes para imprimir");
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Guía DHL - Orden ${order?.id || ""}</title>
          <meta charset="UTF-8">
          <style>
            body { margin: 0; padding: 0; }
            iframe { width: 100%; height: 100vh; border: none; }
          </style>
        </head>
        <body>
          <iframe src="${labelDataUri}"></iframe>
          <script>
            setTimeout(() => { window.print(); }, 1000);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownloadLabel = () => {
    if (!labelDataUri) {
      message.error("No hay guía para descargar");
      return;
    }

    try {
      const fileName = `guia-dhl-${trackingNumber || order?.id || "orden"}.pdf`;

      const link = document.createElement("a");
      link.href = labelDataUri;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Descargando guía: ${fileName}`);
    } catch (error) {
      message.error("Error al descargar la guía");
    }
  };

  const handleCloseLabelModal = () => {
    setLabelModalVisible(false);
  };

  const handleOpenTracking = () => {
    const guideNumbers = getDHLGuideNumbers();
    if (guideNumbers.length > 0) {
      const firstGuide = guideNumbers[0].guideNumber;
      setTrackingNumberState(firstGuide);
      setShipmentId(firstGuide);
      setTrackingModalVisible(true);
    } else {
      message.warning("No hay guía de envío disponible para rastrear");
    }
  };

  if (loadingPage) {
    return (
      <LayoutPage>
        <div className="orders-details-container">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </LayoutPage>
    );
  }

  if (!order || !order.id) {
    return (
      <LayoutPage>
        <div className="orders-details-container">
          <Card>
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Text type="secondary">Cargando información de la orden...</Text>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          </Card>
        </div>
      </LayoutPage>
    );
  }

  const shippingCost = getShippingCost();
  const shippingService = getShippingService();
  const subtotal = getSubtotal();
  const totalAmount = getTotalAmount();

  const getShippingDisplay = () => {
    if (shippingCost !== null && shippingCost > 0) {
      return {
        text: `$${shippingCost.toFixed(2)} MXN`,
        color: "blue",
        type: "cost",
      };
    }
    if (shippingCost !== null && shippingCost === 0) {
      return { text: "GRATIS", color: "green", type: "free" };
    }
    return { text: "Por calcular", color: "default", type: "pending" };
  };

  const shippingDisplay = getShippingDisplay();
  const deliveryAlertMeta = getDeliveryAlertMeta();

  const isClient = role === 4;
  const isAdmin = role === 1 || role === 3 || role === 5;
  const isSupplier = role === 6;

  const canViewShipping = isClient || isAdmin;

  return (
    <LayoutPage>
      <div className="orders-details-container">
        <Modal
          open={isLoginGateModalOpen}
          onCancel={() => {}}
          footer={null}
          centered
          closable={false}
          maskClosable={false}
          keyboard={false}
          width={420}
          bodyStyle={{ padding: 0 }}
        >
          <div
            style={{
              background: "#0F5197",
              color: "#fff",
              padding: "28px 24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.15)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <ShoppingOutlined style={{ fontSize: 28, color: "#fff" }} />
            </div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
              Inicia sesión para continuar
            </h2>
            <p style={{ margin: "8px 0 0", fontSize: 14, opacity: 0.95 }}>
              Para ver el detalle de esta orden, primero debes autenticarte.
            </p>
          </div>
          <div style={{ padding: "18px 24px 24px" }}>
            <Button
              type="primary"
              block
              size="large"
              onClick={handleLoginRedirect}
              style={{
                background: "#0F5197",
                borderColor: "#0F5197",
                fontWeight: 600,
              }}
            >
              Iniciar sesión
            </Button>
            <Button
              block
              size="large"
              onClick={handleRegisterRedirect}
              style={{ marginTop: 12, fontWeight: 600 }}
            >
              Crear cuenta
            </Button>
          </div>
        </Modal>

        <Modal
          title={`Cambiar estado de orden ${order.id}`}
          open={statusModalVisible}
          onCancel={handleStatusModalClose}
          footer={[
            <Button
              key="cancel"
              onClick={handleStatusModalClose}
              disabled={statusChangeLoading}
            >
              Cancelar
            </Button>,
            <Button
              key="reject"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => showStatusConfirm("rechazar", 5)}
              loading={statusChangeLoading}
            >
              Rechazar Orden
            </Button>,
            <Button
              key="accept"
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => showStatusConfirm("aceptar", 1)}
              loading={statusChangeLoading}
            >
              Aceptar Orden
            </Button>,
          ]}
        >
          <div style={{ padding: "20px 0", textAlign: "center" }}>
            <ExclamationCircleOutlined
              style={{
                fontSize: "48px",
                color: "#faad14",
                marginBottom: "16px",
              }}
            />
            <h3>¿Qué acción deseas realizar con esta orden?</h3>
            <p style={{ color: "#666", marginBottom: "0" }}>
              La orden <strong>{order.id}</strong> está actualmente en estado{" "}
              <strong>Pendiente</strong>.
            </p>
          </div>
        </Modal>

        <Modal
          title={
            <div>
              <FilePdfOutlined
                style={{ marginRight: "8px", color: "#ff4d4f" }}
              />
              Guía de Envío DHL
              {trackingNumber && (
                <span
                  style={{
                    fontSize: "12px",
                    marginLeft: "12px",
                    color: "#666",
                  }}
                >
                  Tracking: <strong>{trackingNumber}</strong>
                </span>
              )}
            </div>
          }
          open={labelModalVisible}
          onCancel={handleCloseLabelModal}
          width={480}
          footer={[
            <Button
              key="download"
              icon={<DownloadOutlined />}
              onClick={handleDownloadLabel}
              type="primary"
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
            >
              Descargar Guía
            </Button>,
            <Button
              key="print"
              icon={<PrinterOutlined />}
              onClick={handlePrintLabel}
            >
              Imprimir
            </Button>,
            <Button key="close" onClick={handleCloseLabelModal}>
              Cerrar
            </Button>,
          ]}
        >
          <div style={{ textAlign: "center", padding: "24px 16px" }}>
            <FilePdfOutlined
              style={{
                fontSize: "64px",
                color: "#ff4d4f",
                marginBottom: "16px",
              }}
            />

            <div style={{ marginBottom: "20px" }}>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  margin: "0 0 8px 0",
                }}
              >
                Guía generada exitosamente
              </p>
              <p style={{ margin: "0", color: "#666", fontSize: "14px" }}>
                Número de rastreo:{" "}
                <strong style={{ color: "#1890ff" }}>{trackingNumber}</strong>
              </p>
            </div>

            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#f6ffed",
                borderRadius: "8px",
                border: "1px solid #b7eb8f",
                textAlign: "left",
              }}
            >
              <CheckCircleOutlined
                style={{ color: "#52c41a", marginRight: "8px" }}
              />
              <span style={{ fontSize: "13px", color: "#52c41a" }}>
                La guía está lista para descargar o imprimir
              </span>
              <p
                style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                El archivo se descargará como:{" "}
                <strong>guia-dhl-{trackingNumber || order.id}.pdf</strong>
              </p>
            </div>
          </div>
        </Modal>

        <Modal
          title={
            <div>
              <TruckOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
              Rastreo de Envío DHL
              {trackingNumberState && (
                <span
                  style={{
                    fontSize: "12px",
                    marginLeft: "12px",
                    color: "#666",
                  }}
                >
                  Guía: <strong>{trackingNumberState}</strong>
                </span>
              )}
            </div>
          }
          open={trackingModalVisible}
          onCancel={() => setTrackingModalVisible(false)}
          width="90%"
          style={{ top: 20, maxWidth: "900px" }}
          footer={[
            <Button key="close" onClick={() => setTrackingModalVisible(false)}>
              Cerrar
            </Button>,
          ]}
          className="tracking-modal"
        >
          <TrackingTimeline
            orderId={order.id}
            shipmentId={shipmentId}
            trackingNumber={trackingNumberState}
          />
        </Modal>

        <Card className="order-header-card" variant="borderless">
          <Row gutter={[24, 16]} align="middle" className="header-top-row">
            <Col xs={24} md={12}>
              <div className="order-title-section">
                <Title level={4} className="order-number">
                  Orden #{order.id}
                </Title>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="order-status-header">
                <Space size="middle" className="status-space">
                  <span
                    className="custom-status-badge"
                    style={{ backgroundColor: getBadgeColor(order.status) }}
                  />
                  <Tooltip title={getStatusTooltip()} placement="top">
                    <div
                      onClick={handleStatusClick}
                      style={{
                        cursor: getStatusClickable() ? "pointer" : "default",
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        transition: "all 0.3s",
                      }}
                      onMouseEnter={(e) => {
                        if (getStatusClickable()) {
                          e.currentTarget.style.backgroundColor = "#f0f0f0";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (getStatusClickable()) {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.transform = "scale(1)";
                        }
                      }}
                    >
                      <MarketplaceStatus status={order.status} />
                    </div>
                  </Tooltip>
                  <Divider type="vertical" className="status-divider" />
                  <Text type="secondary" className="order-date">
                    <ClockCircleOutlined />{" "}
                    {dayjs(order.updated_at).format("DD/MM/YYYY HH:mm")}
                  </Text>
                </Space>
              </div>
            </Col>
          </Row>

          {getStatusMessage() && (
            <Row gutter={[24, 16]} className="status-message-row">
              <Col xs={24}>
                <div className="status-message-container">
                  <Text className="status-message-text">
                    {getStatusMessage()}
                  </Text>
                </div>
              </Col>
            </Row>
          )}

          <Row gutter={[24, 16]} className="header-actions">
            <Col xs={24}>
              <div className="actions-container">
                <Space wrap size="middle" className="action-space">
                  {isAdmin && (
                    <Button
                      onClick={() => updateOrderStatus(4)}
                      disabled={!deliveryDate}
                      danger
                      icon={<CloseCircleOutlined />}
                      className="action-button"
                      loading={statusChangeLoading}
                    >
                      Cancelar orden
                    </Button>
                  )}

                  {isSupplier && order.status === 9 && (
                    <Button
                      type="primary"
                      onClick={() => setStatusModalVisible(true)}
                      icon={<CheckOutlined />}
                      className="action-button"
                      loading={statusChangeLoading}
                    >
                      Gestionar orden pendiente
                    </Button>
                  )}

                  {isSupplier && order.status === 2 && !hasDHLGuide() && (
                    <Button
                      onClick={handleGenerateLabel}
                      type="primary"
                      icon={<FilePdfOutlined />}
                      className="action-button"
                      loading={generatingLabel}
                      style={{
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                      }}
                    >
                      {generatingLabel
                        ? "Generando guía..."
                        : "Generar Guía DHL"}
                    </Button>
                  )}

                  {hasDHLGuide() && (
                    <>
                      <Tooltip
                        title={`Número(s) de guía: ${getDHLGuideNumbers()
                          .map((g) => g.guideNumber)
                          .join(", ")}`}
                      >
                        <Button
                          icon={<FilePdfOutlined />}
                          onClick={() => {
                            const firstGuide = getDHLGuideNumbers()[0];
                            setTrackingNumber(firstGuide.guideNumber);
                            message.info(
                              `Guía generada para ${firstGuide.productName}: ${firstGuide.guideNumber}`
                            );
                          }}
                          className="action-button"
                        >
                          Ver Guía Generada
                        </Button>
                      </Tooltip>

                      <Button
                        icon={<TruckOutlined />}
                        onClick={handleOpenTracking}
                        className="action-button"
                        type="default"
                      >
                        Rastrear Envío
                      </Button>
                    </>
                  )}

                  {order.status === 2 && !hasDHLGuide() && (
                    <div
                      style={{
                        padding: "8px 16px",
                        background: isSupplier ? "#e6f7ff" : "#fff7e6",
                        borderRadius: "8px",
                        border: isSupplier
                          ? "1px solid #1890ff"
                          : "1px solid #ffd666",
                      }}
                    >
                      {isSupplier ? (
                        <>
                          <FilePdfOutlined
                            style={{ color: "#1890ff", marginRight: "8px" }}
                          />
                          <span>
                            Debes generar la guía DHL para que el cliente pueda
                            rastrear su paquete.
                          </span>
                        </>
                      ) : (
                        <>
                          <ClockCircleOutlined
                            style={{ color: "#faad14", marginRight: "8px" }}
                          />
                          <span>
                            El proveedor está generando la guía de envío. Pronto
                            podrás rastrear tu paquete.
                          </span>
                        </>
                      )}
                    </div>
                  )}

                  {isSupplier && order.status === 1 && (
                    <Button
                      onClick={handleMarkAsInTransit}
                      type="primary"
                      loading={statusChangeLoading}
                      icon={<SendOutlined />}
                      className="action-button"
                    >
                      Enviar orden
                    </Button>
                  )}

                  {isSupplier && order.status === 2 && (
                    <Button
                      onClick={handleMarkAsDelivered}
                      type="primary"
                      loading={statusChangeLoading}
                      icon={<CheckCircleOutlined />}
                      className="action-button"
                    >
                      Marcar como entregada
                    </Button>
                  )}

                  {isClient && order.status === 2 && (
                    <Button
                      onClick={() => updateOrderStatus(3)}
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      className="action-button"
                      loading={statusChangeLoading}
                    >
                      Confirmar entrega
                    </Button>
                  )}
                </Space>
              </div>
            </Col>
          </Row>
        </Card>

        <Row gutter={[24, 24]} className="main-content">
          <Col xs={24} lg={16}>
            <Card
              variant="borderless"
              className="products-card"
              styles={{ body: { padding: 0 } }}
            >
              <Collapse
                bordered={false}
                expandIconPosition="end"
                onChange={(key) => setActiveKey(key)}
                activeKey={activeKey}
                className="products-collapse"
                expandIcon={({ isActive }) => (
                  <DownOutlined rotate={isActive ? 180 : 0} />
                )}
              >
                <Panel
                  header={
                    <Space>
                      <ShoppingOutlined />
                      <Text strong>Productos</Text>
                      {!activeKey.includes("1") && (
                        <Tag className="products-count-tag">
                          {order.products?.length || 0}
                        </Tag>
                      )}
                    </Space>
                  }
                  key="1"
                >
                  <List
                    itemLayout="horizontal"
                    dataSource={order.products}
                    renderItem={(product, index) => {
                      const supplier = suppliers.find(
                        (s) => s.id === product.supplier_id
                      );
                      return (
                        <List.Item
                          className="product-list-item"
                          actions={[
                            <Text strong className="product-price">
                              ${product.price} MXN
                            </Text>,
                            product.dhl_shipment_id && (
                              <Tag
                                color="green"
                                icon={<TruckOutlined />}
                                style={{ marginLeft: "8px" }}
                              >
                                Guía: {product.dhl_shipment_id}
                              </Tag>
                            ),
                          ]}
                        >
                          <List.Item.Meta
                            avatar={
                              <Avatar
                                shape="square"
                                size={64}
                                src={product.imgs?.[0]}
                                icon={<ShopOutlined />}
                                className="product-avatar"
                              />
                            }
                            title={
                              <Space direction="vertical" size={2}>
                                <Text strong className="product-name">
                                  {product.name}
                                </Text>
                                <Space
                                  split={<Divider type="vertical" />}
                                  size={4}
                                >
                                  <Text type="secondary">
                                    Cantidad: {product.quantity} u.
                                  </Text>
                                  <Text type="secondary">
                                    Proveedor:{" "}
                                    {supplier?.name || "No disponible"}
                                  </Text>
                                </Space>
                              </Space>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                </Panel>
              </Collapse>
            </Card>

            <Card variant="borderless" className="summary-card">
              <div className="summary-content">
                <Row gutter={[16, 16]}>
                  {canViewShipping && (
                    <Col span={24}>
                      <div className="summary-row shipping-row">
                        <Text type="secondary">
                          <TruckOutlined style={{ marginRight: 8 }} />
                          Envío
                        </Text>
                        <Tag
                          color={shippingDisplay.color}
                          className="shipping-tag"
                        >
                          {shippingDisplay.text}
                        </Tag>
                      </div>
                    </Col>
                  )}

                  <Col span={24}>
                    <div className="summary-row subtotal-row">
                      <Text type="secondary">
                        <ShoppingOutlined style={{ marginRight: 8 }} />
                        Subtotal
                      </Text>
                      <Text strong className="subtotal-amount">
                        ${subtotal.toFixed(2)} MXN
                      </Text>
                    </div>
                  </Col>

                  {canViewShipping &&
                    shippingCost !== null &&
                    shippingCost > 0 && (
                      <Col span={24}>
                        <div className="summary-row shipping-cost-row">
                          <Text type="secondary">
                            <DollarOutlined style={{ marginRight: 8 }} />
                            Costo de envío
                          </Text>
                          <Text strong className="shipping-cost-amount">
                            ${shippingCost.toFixed(2)} MXN
                          </Text>
                        </div>
                      </Col>
                    )}

                  {canViewShipping && shippingService && (
                    <Col span={24}>
                      <div className="summary-row shipping-service-row">
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          <TruckOutlined /> Servicio: {shippingService}
                        </Text>
                      </div>
                    </Col>
                  )}

                  <Col span={24}>
                    <Divider className="summary-divider" />
                    <div className="summary-row total-row">
                      <Title level={4} className="total-label">
                        TOTAL
                      </Title>
                      <Title level={3} className="total-amount">
                        ${totalAmount.toFixed(2)} MXN
                      </Title>
                    </div>
                  </Col>

                  <Col span={24}>
                    <div className="delivery-section">
                      <Space align="center" size="middle">
                        <TruckOutlined className="delivery-icon" />
                        <Text strong>Fecha de entrega:</Text>
                        {order.status == 1 ? (
                          isClient || isAdmin ? (
                            <Text type="secondary">
                              El proveedor está confirmando la fecha de entrega.
                            </Text>
                          ) : (
                            <>
                              <DatePicker
                                showTime
                                format="YYYY-MM-DD"
                                onChange={handleDateChange}
                                value={
                                  deliveryDate
                                    ? dayjs(deliveryDate, "YYYY-MM-DD")
                                    : null
                                }
                                disabled={isDeliveryDateLocked}
                                className="delivery-picker"
                                placeholder="Seleccionar fecha"
                              />
                              {deliveryDate && isDeliveryDateLocked && (
                                <Button onClick={handleEnableDeliveryDateEdit}>
                                  Editar fecha
                                </Button>
                              )}
                              {deliveryAlertMeta && (
                                <Tag color={deliveryAlertMeta.tagColor}>
                                  {deliveryAlertMeta.label}
                                </Tag>
                              )}
                            </>
                          )
                        ) : (
                          <>
                            <Text strong className="delivery-date">
                              {order.deliveryDate
                                ? dayjs(order.deliveryDate).format("DD/MM/YYYY")
                                : "No especificada"}
                            </Text>
                            {deliveryAlertMeta && (
                              <Tag color={deliveryAlertMeta.tagColor}>
                                {deliveryAlertMeta.label}
                              </Tag>
                            )}
                          </>
                        )}
                      </Space>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              title={
                <Space>
                  <ClockCircleOutlined />
                  <Text strong>Seguimiento de la orden</Text>
                  {orderLogs.length > 5 && (
                    <Tag className="tracking-count-tag">
                      {orderLogs.length} eventos
                    </Tag>
                  )}
                </Space>
              }
              variant="borderless"
              className="tracking-card"
            >
              <div className="tracking-container">
                {hasDHLGuide() && (
                  <div
                    style={{
                      background: "#e6f7ff",
                      padding: "12px",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      borderLeft: "4px solid #1890ff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <TruckOutlined style={{ color: "#1890ff" }} />
                      <strong>Información de Envío DHL</strong>
                    </div>
                    <div style={{ fontSize: "12px" }}>
                      {getDHLGuideNumbers().map((item, idx) => (
                        <div key={idx} style={{ marginBottom: "4px" }}>
                          <strong>{item.productName}:</strong>{" "}
                          {item.guideNumber}
                        </div>
                      ))}
                      {canViewShipping && shippingService && (
                        <div style={{ marginTop: "8px", color: "#666" }}>
                          Servicio: {shippingService}
                        </div>
                      )}
                      <div style={{ marginTop: "8px" }}>
                        <Button
                          type="link"
                          size="small"
                          icon={<TruckOutlined />}
                          onClick={handleOpenTracking}
                          style={{ paddingLeft: 0 }}
                        >
                          Ver rastreo completo
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {order.status === 2 && !hasDHLGuide() && (
                  <div
                    style={{
                      background: isSupplier ? "#e6f7ff" : "#fff7e6",
                      padding: "12px",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      borderLeft: isSupplier
                        ? "4px solid #1890ff"
                        : "4px solid #faad14",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      {isSupplier ? (
                        <FilePdfOutlined style={{ color: "#1890ff" }} />
                      ) : (
                        <ClockCircleOutlined style={{ color: "#faad14" }} />
                      )}
                      <strong>
                        {isSupplier
                          ? "Pendiente: Generar Guía de Envío"
                          : "En espera de guía de envío"}
                      </strong>
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {isSupplier ? (
                        <>
                          La orden está marcada como "En camino". Debes generar
                          la guía DHL para que el cliente pueda rastrear su
                          paquete.
                          <br />
                          <Button
                            type="link"
                            size="small"
                            icon={<FilePdfOutlined />}
                            onClick={handleGenerateLabel}
                            loading={generatingLabel}
                            style={{ paddingLeft: 0, marginTop: "4px" }}
                          >
                            {generatingLabel
                              ? "Generando guía..."
                              : "Generar Guía DHL ahora"}
                          </Button>
                        </>
                      ) : (
                        "El proveedor está generando la guía de envío. En breve podrás rastrear tu paquete con DHL."
                      )}
                    </div>
                  </div>
                )}

                <Timeline
                  mode="left"
                  items={orderLogs.map((log, index) => ({
                    dot: getStatusIcon(log.status),
                    color:
                      log.status === 4
                        ? "red"
                        : log.status === 3
                        ? "green"
                        : log.status === 2
                        ? "orange"
                        : "orange",
                    children: (
                      <div className="timeline-content">
                        <div className="timeline-status">
                          <MarketplaceStatus status={log.status} />
                        </div>
                        <div className="timeline-date">
                          <Text type="secondary">
                            {dayjs(log.updatedAt).format("DD/MM/YYYY HH:mm")}
                          </Text>
                        </div>
                      </div>
                    ),
                  }))}
                  className="custom-timeline"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </LayoutPage>
  );
};

export default OrdersDetails;
